import { mat4, quat, vec2, vec3, vec4 } from "gl-matrix";
import {
  BACKEND_URL,
  DIFFUSE_TEXTURES,
  NORMAL_TEXTURES,
} from "../../constants";
import Common from "../CommonRender";
import { ProgramInfo } from "../ProgramInfo";
import { Shader } from "../Shader";
import { getExportedShaderType, ShaderManager } from "../ShaderManager";
import { TextureManager } from "../TextureManager";
import { defaultMaterial, ExportedMaterial, Material, ObjectBuffers, ObjectType, ObjectTypeToString } from "./types";

export type RenderObjectExportValues = {
  name: string;
  material: ExportedMaterial;
  type: string;
  position: number[];
  scale: number[];
  rotation: number[];
  rotationEulers: {
    x: number;
    y: number;
    z: number;
  };
  parent: string | null;
  model: string | null;
  fileName?: string;
}

export interface Model {
  uvs: number[];
  vertices: number[];
  triangles: number[];
  normals: number[];
  tangents: number[],
  bitangents: number[],

  // transform values
  position: vec3;
  scale: vec3;
  rotation: quat;
  rotationInputs: {
    x: number;
    y: number;
    z: number;
  }
}

export type RenderObjectConstructorArgs = {
  name: string;
  position?: vec3;
  scale?: vec3;
  rotation?: quat;
  rotationEulers?: {
    x: number;
    y: number;
    z: number;
  };
  diffuseTextureFile?: string;
  normalTextureFile?: string;
}

export class RenderObject {
  name: string;
  rotationValues: [number, number, number] = [0.0, 0.0, 0.0];
  scaleValues: [number, number, number] = [0.0, 0.0, 0.0];
  model: Model = {
    uvs: [],
    vertices: [],
    triangles: [],
    normals: [],
    tangents: [],
    bitangents: [],

    position: vec3.create(),
    scale: vec3.fromValues(1, 1, 1),
    rotation: quat.create(),
    rotationInputs: {
      x: 0,
      y: 0,
      z: 0,
    }
  };
  type: ObjectType;
  programInfo?: ProgramInfo;
  centroid?: vec3;
  material?: Material;
  collide?: boolean;
  reflective?: boolean;
  refractionIndex?: number;
  parent?: RenderObject;
  modelName?: string;
  buffers?: ObjectBuffers;
  diffuseTextureFile?: string;
  normalTextureFile?: string;
  id?: [number, number, number, number]; // used for picker identification (RGBA)

  constructor(object: RenderObjectConstructorArgs) {
    this.name = object.name;
    this.type = ObjectType.UNSPECIFIED;

    if (object.position)
      this.model.position = object.position;
    if (object.scale)
      this.model.scale = object.scale;
    if (object.rotation)
      this.model.rotation = object.rotation;
    if (object.rotationEulers)
      this.model.rotationInputs = object.rotationEulers;
    this.diffuseTextureFile = object.diffuseTextureFile ?? "defaultDiffuse";
    this.normalTextureFile = object.normalTextureFile ?? "defaultNormal";
  }

  getRotationValues() {
    return this.rotationValues;
  }

  getScaleValues() {
    return this.scaleValues;
  }

  setRotationValues(values: [number, number, number]) {
    this.rotationValues = values;
  }

  setScaleValues(values: [number, number, number]) {
    this.scaleValues = values;
  }

  getCentroid(): vec4 {
    if (this.centroid) {
      return vec4.fromValues(
        this.centroid[0],
        this.centroid[1],
        this.centroid[2],
        1.0,
      );
    }
    console.warn("centroid is not initialized for this object");
    return vec4.fromValues(
      0,
      0,
      0,
      1.0,
    );
  }

  setMaterial(mat: Material) {
    this.material = mat;
  }

  initIndexBuffer(gl: WebGLRenderingContext, elementArray: Uint16Array | Uint32Array) {
    const indexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, elementArray, gl.STATIC_DRAW);
    return indexBuffer;
  }

  async loadTexture(tid: string): Promise<string | undefined> {
    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(`${BACKEND_URL}/textures/${tid}`, {
        headers: {
          Authorization: `${token}`,
        },
      });
      if (!response.ok) throw new Error("Load Texture Failed");

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error loading texture:", error);
    }
  }

  getTextures(
    gl: WebGLRenderingContext,
    imgPath: string,
    tid?: string,
    mesh = false,
  ): Promise<WebGLTexture> {
    return new Promise(async (resolve, reject) => {
      let path = "";

      if (DIFFUSE_TEXTURES.includes(imgPath) || NORMAL_TEXTURES.includes(imgPath)) {
        path = mesh ? `./models/${imgPath}` : `./materials/${imgPath}`;
      } else {
        if (tid) {
          const textureUrl = await this.loadTexture(tid);
          path = textureUrl ?? "./materials/default.jpg";
        } else {
          path = "./materials/default.jpg";
        }
      }

      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        1,
        1,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        new Uint8Array([255, 0, 0, 255]),
      ); // placeholder red pixel

      const image = new Image();
      image.src = path;
      image.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          image,
        );
        resolve(texture!);
      };
      image.onerror = reject;
    });
  }

  calculateCentroid(vertices: number[]) {
    const center = vec3.create();
    for (let t = 0; t < vertices.length; t += 3) {
      vec3.add(center, center, vec3.fromValues(vertices[t], vertices[t + 1], vertices[t + 2]));
    }
    vec3.scale(center, center, 1 / (vertices.length / 3));
    this.centroid = center;
  }

  initPositionAttribute(gl: WebGLRenderingContext, programInfo: ProgramInfo, positionArray: Float32Array) {
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positionArray, gl.STATIC_DRAW);

    gl.vertexAttribPointer(
      programInfo.attribLocations.vertexPosition,
      3,
      gl.FLOAT,
      false,
      0,
      0,
    );
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);

    return positionBuffer;
  }

  initNormalAttribute(gl: WebGLRenderingContext, programInfo: ProgramInfo, normalArray: Float32Array) {
    const normalBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, normalArray, gl.STATIC_DRAW);

    gl.vertexAttribPointer(
      programInfo.attribLocations.vertexNormal,
      3,
      gl.FLOAT,
      false,
      0,
      0,
    );
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);

    return normalBuffer;
  }

  initTextureCoords(gl: WebGLRenderingContext, programInfo: ProgramInfo, textureCoords: Float32Array) {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, textureCoords, gl.STATIC_DRAW);

    gl.vertexAttribPointer(
      programInfo.attribLocations.vertexUV,
      2,
      gl.FLOAT,
      false,
      0,
      0,
    );
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexUV);

    return buffer;
  }

  initTangentBuffer(gl: WebGLRenderingContext, programInfo: ProgramInfo, tangents: Float32Array) {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, tangents, gl.STATIC_DRAW);

    gl.vertexAttribPointer(
      programInfo.attribLocations.vertexTangent,
      3,
      gl.FLOAT,
      false,
      0,
      0,
    );
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexTangent);

    return buffer;
  }

  initBitangentBuffer(gl: WebGLRenderingContext, programInfo: ProgramInfo, bitangents: Float32Array) {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, bitangents, gl.STATIC_DRAW);

    gl.vertexAttribPointer(
      programInfo.attribLocations.vertexBitangent,
      3,
      gl.FLOAT,
      false,
      0,
      0,
    );
    gl.enableVertexAttribArray(programInfo.attribLocations.vertexBitangent);

    return buffer;
  }

  initBuffers(gl: WebGL2RenderingContext) {
    //create vertices, normal and indicies arrays
    const positions = new Float32Array(this.model.vertices);
    const normals = new Float32Array(this.model.normals);
    const indices = new Uint16Array(this.model.triangles);
    const textureCoords = new Float32Array(this.model.uvs);
    const tangents = new Float32Array(this.model.tangents.flat());
    const bitangents = new Float32Array(this.model.bitangents.flat());

    if (!this.programInfo) {
      throw new Error("cannot initBuffers, missing programInfo");
    }

    const vertexArrayObject = gl.createVertexArray();
    if (!vertexArrayObject)
      throw new Error("error initializing vao");
    gl.bindVertexArray(vertexArrayObject);

    const positionBuffer = this.initPositionAttribute(gl, this.programInfo, positions);
    const indexBuffer = this.initIndexBuffer(gl, indices);

    if (!positionBuffer || !indexBuffer) {
      throw new Error("error initializing buffers");
    }

    this.buffers = {
      vao: vertexArrayObject,
      attributes: {
        position: positionBuffer,
        normal:
          this.programInfo.attribLocations.vertexNormal != null
            ? this.initNormalAttribute(gl, this.programInfo, normals) ?? undefined
            : undefined,
        uv:
          this.programInfo.attribLocations.vertexUV != null
            ? this.initTextureCoords(gl, this.programInfo, textureCoords) ?? undefined
            : undefined,
        tangents:
          this.programInfo.attribLocations.vertexTangent != null
            ? this.initTangentBuffer(gl, this.programInfo, tangents) ?? undefined
            : undefined,
        bitangents:
          this.programInfo.attribLocations.vertexBitangent != null
            ? this.initBitangentBuffer(gl, this.programInfo, bitangents) ?? undefined
            : undefined,
      },
      indicies: indexBuffer,
      numVertices: indices.length > 0 ? indices.length : this.model.vertices.length,
    };
  }

  initShader(gl: WebGL2RenderingContext, shaderManager: ShaderManager) {
    let programInfo: ProgramInfo;
    let shader: Shader;

    if (!this.material?.shaderType) {
      throw new Error("missing shader type on material");
    }

    shader = shaderManager.getShader(this.material?.shaderType);
    if (!shader.program) {
      throw new Error("missing shader program");
    }

    programInfo = Common.initShaderUniforms(
      gl,
      shader.program,
      shader.uniforms,
      shader.attribs,
    );
    this.programInfo = programInfo;
  }

  setup(gl: WebGL2RenderingContext, shaderManager: ShaderManager, textureManager: TextureManager) {
    // set the texture names if they're present in the material
    if (this.material?.diffuseTexture) {
      this.diffuseTextureFile = this.material?.diffuseTexture.name;
    }
    if (this.material?.normalTexture) {
      this.normalTextureFile = this.material?.normalTexture.name;
    }
  }

  getVertexRowN(vertices: number[], n: number): vec3 {
    return vec3.fromValues(
      vertices[n * 3],
      vertices[n * 3 + 1],
      vertices[n * 3 + 2],
    );
  }

  getUVRowN(uvs: number[], n: number): vec2 {
    return vec2.fromValues(
      uvs[n * 2],
      uvs[n * 2 + 1],
    );
  }

  getModelMatrix(): mat4 {
    const modelMatrix = mat4.create();

    // Apply rotation
    const rotationMatrix = mat4.create();
    mat4.fromQuat(rotationMatrix, this.model.rotation);

    // Combine translation * rotation * scale
    let negCentroid;
    if (this.centroid) {
      negCentroid = vec3.fromValues(0.0, 0.0, 0.0);
      vec3.negate(negCentroid, this.centroid);
    }

    mat4.translate(modelMatrix, modelMatrix, this.model.position);
    if (this.centroid)
      mat4.translate(modelMatrix, modelMatrix, this.centroid);
    mat4.multiply(modelMatrix, modelMatrix, rotationMatrix);
    mat4.scale(modelMatrix, modelMatrix, this.model.scale);

    if (negCentroid)
      mat4.translate(modelMatrix, modelMatrix, negCentroid);

    // TODO - handle parent model matrix relation
    if (this.parent) {
      if (this.parent.model && this.parent.getModelMatrix()) {
        mat4.multiply(modelMatrix, this.parent.getModelMatrix(), modelMatrix);
      }
    }

    // TODO - handle bounding model matrix multiplication



    return modelMatrix;
  }
  calculateBitangents(hasIndices = true): { bitangents: number[][], tangents: number[][] } {
    const { vertices, uvs, triangles } = this.model;

    const bitangents: number[][] = [];
    const tangents: number[][] = [];

    if (hasIndices) {
      for (let i = 0; i < triangles.length; i += 3) {
        const index1 = triangles[i];
        const index2 = triangles[i + 1];
        const index3 = triangles[i + 2];

        const pos1: vec3 = [
          vertices[index1 * 3],
          vertices[index1 * 3 + 1],
          vertices[index1 * 3 + 2],
        ];
        const pos2: vec3 = [
          vertices[index2 * 3],
          vertices[index2 * 3 + 1],
          vertices[index2 * 3 + 2],
        ];
        const pos3: vec3 = [
          vertices[index3 * 3],
          vertices[index3 * 3 + 1],
          vertices[index3 * 3 + 2],
        ];

        const uv1: vec2 = [uvs[index1 * 2], uvs[index1 * 2 + 1]];
        const uv2: vec2 = [uvs[index2 * 2], uvs[index2 * 2 + 1]];
        const uv3: vec2 = [uvs[index3 * 2], uvs[index3 * 2 + 1]];

        const edge1 = vec3.subtract(vec3.create(), pos2, pos1);
        const edge2 = vec3.subtract(vec3.create(), pos3, pos1);
        const deltaUV1 = vec2.subtract(vec2.create(), uv2, uv1);
        const deltaUV2 = vec2.subtract(vec2.create(), uv3, uv1);

        const denom = deltaUV1[0] * deltaUV2[1] - deltaUV2[0] * deltaUV1[1];
        const f = 1.0 / (Math.abs(denom) > 1e-6 ? denom : 1e-6); // Safe inversion

        const tangent = vec3.normalize(
          vec3.create(),
          vec3.scale(
            vec3.create(),
            vec3.subtract(
              vec3.create(),
              vec3.scale(vec3.create(), edge1, deltaUV2[1]),
              vec3.scale(vec3.create(), edge2, deltaUV1[1])
            ),
            f
          )
        );

        const bitangent = vec3.normalize(
          vec3.create(),
          vec3.scale(
            vec3.create(),
            vec3.subtract(
              vec3.create(),
              vec3.scale(vec3.create(), edge2, deltaUV1[0]),
              vec3.scale(vec3.create(), edge1, deltaUV2[0])
            ),
            f
          )
        );

        // Push same values for each vertex of the triangle
        for (let j = 0; j < 3; j++) {
          tangents.push([tangent[0], tangent[1], tangent[2]]);
          bitangents.push([bitangent[0], bitangent[1], bitangent[2]]);
        }
      }
    }

    return { bitangents, tangents };
  }

  clone(name: string): RenderObject {
    return new (this.constructor as { new(...args: any[]): RenderObject })(name);
  }

  delete(gl: WebGL2RenderingContext) {
    // delete all buffers
    if (this.buffers?.attributes.position)
      gl.deleteBuffer(this.buffers?.attributes.position);
    if (this.buffers?.attributes.normal)
      gl.deleteBuffer(this.buffers?.attributes.normal);
    if (this.buffers?.attributes.uv)
      gl.deleteBuffer(this.buffers?.attributes.uv);
    if (this.buffers?.attributes.tangents)
      gl.deleteBuffer(this.buffers?.attributes.tangents);
    if (this.buffers?.attributes.bitangents)
      gl.deleteBuffer(this.buffers?.attributes.bitangents);
    if (this.buffers?.indicies) {
      gl.deleteBuffer(this.buffers.indicies);
    }

    // delete vaos/vbos
    if (this.buffers?.vao)
      gl.deleteVertexArray(this.buffers?.vao)
  }

  export(): RenderObjectExportValues {
    const rotation = mat4.create();
    mat4.fromQuat(rotation, this.model.rotation);

    const mat = this.material ?? defaultMaterial;
    const exportedMat: ExportedMaterial = {
      ...mat,
      shaderType: getExportedShaderType(mat.shaderType),
      ambient: [mat.ambient[0], mat.ambient[1], mat.ambient[2]],
      diffuse: [mat.diffuse[0], mat.diffuse[1], mat.diffuse[2]],
      specular: [mat.specular[0], mat.specular[1], mat.specular[2]],
    }

    return {
      name: this.name,
      material: exportedMat,
      type: ObjectTypeToString(this.type),
      position: Array.from(this.model.position),
      scale: Array.from(this.model.scale),
      rotation: Array.from(rotation),
      rotationEulers: this.model.rotationInputs,
      parent: this.parent?.name ?? null,
      model: null,
    }
  }
}
